import React, { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Check, Loader2 } from 'lucide-react';

/**
 * Carte Premium de la page /pricing (FR) uniquement.
 * Le PricingModal et la landing continuent d'utiliser `PricingPlanCards`.
 */

/** Montants affichés sur /pricing. L'annuel est facturé 228 € (soit 19 €/mois). */
export const PRICING_PREMIUM_AMOUNTS = { monthly: 29, yearly: 19 } as const;

export const PRICING_PREMIUM_ANNUAL_TOTAL = 228;

/** Prix annuel « barré » : 12 mois au tarif mensuel. */
export const PRICING_PREMIUM_ANNUAL_FULL = PRICING_PREMIUM_AMOUNTS.monthly * 12;

/** Économie de l'annuel, dérivée des montants pour ne jamais dériver : 29×12 − 228. */
export const PRICING_PREMIUM_ANNUAL_SAVINGS =
  PRICING_PREMIUM_ANNUAL_FULL - PRICING_PREMIUM_ANNUAL_TOTAL;

/** Espace insécable avant l'euro : « 19 € » ne doit jamais se couper en fin de ligne. */
export const formatEuro = (amount: number) => `${amount}\u00A0€`;

/** Nombre de fonctionnalités détaillées dans la table de comparaison, plus bas. */
const TOTAL_FEATURE_COUNT = 25;

/** Ancre de cette table de comparaison, définie dans `src/pages/Pricing.tsx`. */
const FEATURES_SECTION_ID = 'pricing-features-section';

interface PremiumOfferFeature {
  title: string;
  /** Bénéfice affiché en dessous, en gris — même traitement que le PricingModal. */
  description?: string;
}

// 6 features maximum : au-delà, la charge cognitive fait décrocher, et la liste
// complète est de toute façon dans la table de comparaison plus bas.
// Contenu traduit — voir `public.pricing.premium_offer.features` dans le composant.

const ArrowRightIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </svg>
);

/** Anime le passage 29 → 19 (et retour) quand l'utilisateur bascule le toggle. */
function useAnimatedAmount(target: number): number {
  const [display, setDisplay] = useState(target);
  const previousRef = useRef(target);

  useEffect(() => {
    const from = previousRef.current;
    if (from === target) {
      setDisplay(target);
      return;
    }

    const startTime = Date.now();
    let frame = 0;

    const tick = () => {
      const progress = Math.min((Date.now() - startTime) / 500, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(from + (target - from) * eased));

      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      } else {
        previousRef.current = target;
      }
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target]);

  return display;
}

export interface PricingPremiumOfferCardProps {
  billingInterval: 'monthly' | 'yearly';
  ctaLabel: string;
  onCtaClick: () => void;
  ctaDisabled?: boolean;
  loading?: boolean;
  onCtaHover?: (hover: boolean) => void;
  /** Masque le lien « Voir les N fonctionnalités » — pas de table de comparaison à cibler hors de /pricing. */
  showSeeAllLink?: boolean;
}

export function PricingPremiumOfferCard({
  billingInterval,
  ctaLabel,
  onCtaClick,
  ctaDisabled = false,
  loading = false,
  onCtaHover,
  showSeeAllLink = true,
}: PricingPremiumOfferCardProps) {
  const { t } = useTranslation();
  const isYearly = billingInterval === 'yearly';
  const amount = useAnimatedAmount(PRICING_PREMIUM_AMOUNTS[isYearly ? 'yearly' : 'monthly']);
  const premiumOfferFeatures = t('public.pricing.premium_offer.features', { returnObjects: true }) as PremiumOfferFeature[];

  return (
    <div
      className="relative rounded-3xl p-6 sm:p-8 flex flex-col w-full mx-auto"
      style={{
        maxWidth: '450px',
        background:
          'linear-gradient(135deg, #121212 0%, #221a38 25%, #121212 50%, #1a2847 75%, #121212 100%)',
        border: 'none',
        zIndex: 1,
      }}
    >
      {/* Même traitement typographique que le titre « BASIC » de la carte gratuite. */}
      <h3 className="text-[1.7rem] font-bold text-center" style={{ margin: 0 }}>
        <span
          style={{
            backgroundImage:
              'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)',
            backgroundSize: '200% 200%',
            animation: 'gradientShift 4s ease infinite',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          {t('landing.plans.premium')}
        </span>
      </h3>

      <div className="flex items-baseline justify-center gap-2 mt-4">
        <span
          className="font-extrabold leading-none"
          style={{ color: '#ffffff', fontSize: 'clamp(3rem, 9vw, 4.25rem)', letterSpacing: '-0.03em' }}
        >
          {formatEuro(amount)}
        </span>
        <span className="text-xl sm:text-2xl font-bold" style={{ color: '#9ca3af' }}>
          {t('landing.plans.per_month')}
        </span>
      </div>

      {isYearly ? (
        <>
          <p
            className="flex items-baseline justify-center gap-2 text-center"
            style={{ fontSize: '0.95rem', margin: '1rem 0 0' }}
          >
            <span style={{ color: 'rgba(255,255,255,0.35)', textDecoration: 'line-through' }}>
              {formatEuro(PRICING_PREMIUM_ANNUAL_FULL)}
            </span>
            <span style={{ color: '#ffffff', fontWeight: 600 }}>
              {formatEuro(PRICING_PREMIUM_ANNUAL_TOTAL)} {t('public.pricing.premium_offer.annual_per_year_suffix')}
            </span>
          </p>
          <p className="text-center" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', margin: '0.35rem 0 0' }}>
            {formatEuro(PRICING_PREMIUM_ANNUAL_TOTAL)}{t('public.pricing.premium_offer.annual_note_suffix')}
          </p>
        </>
      ) : (
        <p className="text-center" style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.95rem', margin: '1rem 0 0' }}>
          {t('public.pricing.premium_offer.monthly_note', { amount: formatEuro(PRICING_PREMIUM_ANNUAL_SAVINGS) })}
        </p>
      )}

      <button
        type="button"
        className="premium-cta-button bento-button w-full mt-7 px-5 relative z-10"
        style={{
          background:
            'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)',
          backgroundSize: '200% 200%',
          animation: 'gradientShift 4s ease infinite',
          borderRadius: '9999px',
          color: '#ffffff',
          fontSize: '0.875rem',
          fontWeight: 600,
          paddingTop: '0.9rem',
          paddingBottom: '0.9rem',
        }}
        onClick={onCtaClick}
        onMouseEnter={() => onCtaHover?.(true)}
        onMouseLeave={() => onCtaHover?.(false)}
        disabled={loading || ctaDisabled}
      >
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin mx-auto" />
        ) : (
          <span className="inline-flex items-center justify-center gap-2.5">
            {ctaLabel}
            <ArrowRightIcon />
          </span>
        )}
      </button>

      {/* Liste au format du PricingModal : titre puis bénéfice, avec la même coche que la carte gratuite. */}
      <ul className="space-y-3.5 text-left" style={{ listStyle: 'none', margin: '1.75rem 0 0', padding: 0 }}>
        {premiumOfferFeatures.map((feature) => (
          <li key={feature.title} className="flex items-start gap-2.5">
            <Check className="h-4 w-4 text-purple-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[13.5px] font-semibold leading-snug" style={{ color: '#ffffff', margin: 0 }}>
                {feature.title}
              </p>
              {feature.description && (
                <p className="text-[12px] leading-snug mt-0.5" style={{ color: '#9ca3af', margin: '2px 0 0' }}>
                  {feature.description}
                </p>
              )}
            </div>
          </li>
        ))}
        {showSeeAllLink && (
          <li className="pt-1">
            <button
              type="button"
              className="text-[13px] font-semibold"
              style={{
                background: 'transparent',
                border: 'none',
                borderBottom: '1px dotted #EC4899',
                padding: '0 0 2px',
                cursor: 'pointer',
                backgroundImage:
                  'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)',
                backgroundSize: '200% 200%',
                animation: 'gradientShift 4s ease infinite',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
              onClick={() =>
                document.getElementById(FEATURES_SECTION_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }
            >
              {t('public.pricing.premium_offer.see_all_link', { count: TOTAL_FEATURE_COUNT })}
            </button>
          </li>
        )}
      </ul>

      <p
        className="text-center"
        style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.78rem', margin: '1.75rem 0 0' }}
      >
        {t('public.pricing.premium_offer.already_used_prefix')} {t('public.pricing.hero.social_proof_count')} {t('public.pricing.premium_offer.already_used_suffix')}
      </p>
    </div>
  );
}

export default PricingPremiumOfferCard;
